import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate, type Identifier } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import {
  availabilityWindows,
  isCoveredByAvailability,
  weekdayOf,
  type TimeWindow,
} from "./guardiasRules";
import {
  FIELD_CLASS,
  GUARDIAS_KEY,
  HELP_CLASS,
  QueryBoundary,
  SectionCard,
  useGuardiasSave,
  type SectionAdvisor,
} from "./guardiasShared";

const windowText = (window: TimeWindow) => `${window.start} - ${window.end}`;

/**
 * "Agregar una guardia" (editors only). The form offers only the availability the chosen advisor
 * marked for that day, so a Team Leader can never impose a guardia; when the database still
 * refuses one, its Spanish validation message is shown as it comes.
 */
export const GuardiasRosterForm = ({
  projectId,
  advisors,
  dates,
  ownId,
}: {
  /** The signed-in sales id: the insert policy requires `created_by` to be the caller. */
  ownId: Identifier | undefined;
  projectId: SectionAdvisor["id"];
  advisors: SectionAdvisor[];
  dates: string[];
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { saving, run } = useGuardiasSave();
  const [chosenAdvisor, setChosenAdvisor] = useState("");
  const [chosenDate, setChosenDate] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [error, setError] = useState<string | null>(null);

  const blocks = useQuery({
    queryKey: [GUARDIAS_KEY, projectId, "blocks"],
    queryFn: () => dataProvider.getAdvisorAvailabilityBlocks(),
  });

  const salesId =
    advisors.find((advisor) => String(advisor.id) === chosenAdvisor)?.id ??
    advisors[0]?.id;
  const date = dates.includes(chosenDate) ? chosenDate : dates[0];

  const pickWindow = (window: TimeWindow | undefined) => {
    setStart(window?.start ?? "");
    setEnd(window?.end ?? "");
    setError(null);
  };

  return (
    <SectionCard title={translate("resources.guardias.form_title")}>
      <QueryBoundary query={blocks}>
        {(allBlocks) => {
          const windows =
            salesId == null
              ? []
              : availabilityWindows(allBlocks, salesId, weekdayOf(date));
          const onSubmit = async (event: FormEvent) => {
            event.preventDefault();
            if (salesId == null || ownId == null) return;
            if (!isCoveredByAvailability(windows, start, end)) {
              setError(translate("resources.guardias.form_outside"));
              return;
            }
            const failure = await run(
              () =>
                dataProvider.create("project_guardias", {
                  data: {
                    project_id: projectId,
                    sales_id: salesId,
                    created_by: ownId,
                    guardia_date: date,
                    start_time: `${start}:00`,
                    end_time: `${end}:00`,
                  },
                }),
              false,
            );
            setError(
              failure === null
                ? null
                : failure || translate("resources.guardias.form_failed"),
            );
          };

          return (
            <form className="space-y-3" onSubmit={onSubmit} noValidate>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="guardia-advisor">
                    {translate("resources.guardias.form_advisor")}
                  </Label>
                  <select
                    id="guardia-advisor"
                    className={FIELD_CLASS}
                    value={salesId == null ? "" : String(salesId)}
                    onChange={(e) => {
                      setChosenAdvisor(e.target.value);
                      pickWindow(undefined);
                    }}
                  >
                    {advisors.map((advisor) => (
                      <option key={advisor.id} value={String(advisor.id)}>
                        {advisor.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guardia-day">
                    {translate("resources.guardias.form_day")}
                  </Label>
                  <select
                    id="guardia-day"
                    className={FIELD_CLASS}
                    value={date}
                    onChange={(e) => {
                      setChosenDate(e.target.value);
                      pickWindow(undefined);
                    }}
                  >
                    {dates.map((value) => (
                      <option key={value} value={value}>
                        {translate(
                          `resources.guardias.weekday.${weekdayOf(value)}`,
                        )}{" "}
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {windows.length === 0 ? (
                <p className={HELP_CLASS}>
                  {translate("resources.guardias.form_no_availability")}
                </p>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="guardia-window">
                      {translate("resources.guardias.form_window")}
                    </Label>
                    <select
                      id="guardia-window"
                      className={FIELD_CLASS}
                      value={
                        windows.find(
                          (window) =>
                            window.start === start && window.end === end,
                        )
                          ? `${start}-${end}`
                          : ""
                      }
                      onChange={(e) =>
                        pickWindow(
                          windows.find(
                            (window) =>
                              `${window.start}-${window.end}` ===
                              e.target.value,
                          ),
                        )
                      }
                    >
                      <option value="" />
                      {windows.map((window) => (
                        <option
                          key={windowText(window)}
                          value={`${window.start}-${window.end}`}
                        >
                          {windowText(window)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="guardia-start">
                        {translate("resources.guardias.form_start")}
                      </Label>
                      <Input
                        id="guardia-start"
                        type="time"
                        className="h-11 text-base"
                        value={start}
                        onChange={(e) => setStart(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guardia-end">
                        {translate("resources.guardias.form_end")}
                      </Label>
                      <Input
                        id="guardia-end"
                        type="time"
                        className="h-11 text-base"
                        value={end}
                        onChange={(e) => setEnd(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {error ? (
                <p role="alert" className="text-[15px] text-destructive">
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                className="h-11"
                disabled={saving || salesId == null || windows.length === 0}
              >
                {translate("resources.guardias.form_add")}
              </Button>
            </form>
          );
        }}
      </QueryBoundary>
    </SectionCard>
  );
};
